import {
  GoogleGenAI,
  LiveServerMessage,
  Modality,
  FunctionDeclaration,
  Type,
} from "@google/genai";
import { useState, useRef, useCallback } from "react";

import { DJMode, DJPersona } from "../types";

// Extend Window for Safari support
interface WindowWithWebKit extends Window {
  webkitAudioContext?: typeof AudioContext;
}

const windowWithWebKit = window as WindowWithWebKit;

// Audio Utilities

// Helper to downsample any sample rate to 16kHz for Gemini
const downsampleTo16k = (
  buffer: Float32Array,
  sampleRate: number,
): Float32Array => {
  if (sampleRate === 16000) return buffer;
  const ratio = sampleRate / 16000;
  const newLength = Math.ceil(buffer.length / ratio);
  const result = new Float32Array(newLength);
  for (let i = 0; i < newLength; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.floor((i + 1) * ratio);
    let sum = 0;
    let count = 0;
    for (let j = start; j < end && j < buffer.length; j++) {
      sum += buffer[j];
      count++;
    }
    result[i] = count > 0 ? sum / count : buffer[start];
  }
  return result;
};

const createBlob = (data: Float32Array): { data: string; mimeType: string } => {
  const l = data.length;
  const int16 = new Int16Array(l);
  for (let i = 0; i < l; i++) {
    // Clamp values to -1..1 to prevent overflow wrapping
    const val = Math.max(-1, Math.min(1, data[i]));
    int16[i] = val * 32768;
  }

  let binary = "";
  const len = int16.buffer.byteLength;
  const bytes = new Uint8Array(int16.buffer);
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);

  return {
    data: base64,
    mimeType: "audio/pcm;rate=16000",
  };
};

const decodeAudioData = async (
  base64: string,
  ctx: AudioContext,
  sampleRate: number,
): Promise<AudioBuffer> => {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const dataInt16 = new Int16Array(bytes.buffer);
  const frameCount = dataInt16.length;
  // Create buffer with the specific sample rate of the audio (24k from Gemini)
  // The context will handle playing this correctly even if system is 48k
  const buffer = ctx.createBuffer(1, frameCount, sampleRate);
  const channelData = buffer.getChannelData(0);

  for (let i = 0; i < frameCount; i++) {
    channelData[i] = dataInt16[i] / 32768.0;
  }
  return buffer;
};

// Tool Definition
const tuneStationTool: FunctionDeclaration = {
  name: "tuneStation",
  parameters: {
    type: Type.OBJECT,
    description:
      "Tune into or recommend a specific type of radio station based on genre, country, or name. Use this when the user asks for recommendations.",
    properties: {
      genre: {
        type: Type.STRING,
        description: "Music genre (e.g., jazz, rock, news, pop)",
      },
      country: {
        type: Type.STRING,
        description: "Country name (e.g., France, Japan, USA)",
      },
      mood: {
        type: Type.STRING,
        description: "Mood (e.g., chill, party, upbeat)",
      },
    },
    required: [],
  },
};

interface UseLiveDJProps {
  onStationChangeRequest: (criteria: {
    genre?: string;
    country?: string;
    mood?: string;
  }) => void;
  persona: DJPersona;
  weatherContext?: string;
  locationContext?: string;
}

export const useLiveDJ = ({
  onStationChangeRequest,
  persona,
  weatherContext,
  locationContext,
}: UseLiveDJProps) => {
  const [mode, setMode] = useState<DJMode>(DJMode.IDLE);
  const [isConnected, setIsConnected] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0); // For visualizer

  const audioContextRef = useRef<AudioContext | null>(null);
  const inputContextRef = useRef<AudioContext | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sessionPromiseRef = useRef<Promise<any> | null>(null);
  const nextStartTimeRef = useRef<number>(0);
  const sourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());

  const connect = useCallback(async () => {
    const apiKey = process.env.API_KEY;
    if (!apiKey) {
      console.error("No API Key");
      return;
    }

    setMode(DJMode.LISTENING);
    setIsConnected(true);

    const ai = new GoogleGenAI({ apiKey });

    // Input Audio Setup - Let browser decide sample rate to avoid NotSupportedError
    const inputAudioContext = new (
      window.AudioContext || windowWithWebKit.webkitAudioContext!
    )();
    inputContextRef.current = inputAudioContext;

    // Output Audio Setup - Let browser decide sample rate
    const outputAudioContext = new (
      window.AudioContext || windowWithWebKit.webkitAudioContext!
    )();
    audioContextRef.current = outputAudioContext;

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // Configuration based on persona
    let systemInstruction =
      "You are DJ Eater, a world-famous AI Radio DJ broadcasting globally. ";
    let voiceName = "Fenrir";

    if (persona === "upbeat") {
      systemInstruction +=
        "Your personality is energetic, hype, and cool. Keep it fast.";
      voiceName = "Fenrir";
    } else if (persona === "chill") {
      systemInstruction +=
        "Your personality is relaxed, zen, and smooth. Speak softly and calmly.";
      voiceName = "Kore";
    } else if (persona === "knowledgeable") {
      systemInstruction +=
        "Your personality is professional, factual, and deep. Focus on music history.";
      voiceName = "Charon";
    }

    // Inject Context
    if (weatherContext || locationContext) {
      systemInstruction += ` Context: The listener is in ${locationContext || "an unknown location"}. The current weather there is ${weatherContext || "unknown"}. Mention this occasionally to feel present.`;
    }

    systemInstruction += ` When a user asks for a type of music, country, or specific request, YOU MUST use the 'tuneStation' tool to change the channel immediately. 
    Announce what you are looking for before you trigger the tool. 
    Don't talk too long, let the music play.`;

    const sessionPromise = ai.live.connect({
      model: "gemini-2.5-flash-native-audio-preview-09-2025",
      callbacks: {
        onopen: () => {
          console.log("Gemini Live Connected");

          const source = inputAudioContext.createMediaStreamSource(stream);
          // Use a standard buffer size. 4096 is ~85ms at 48kHz.
          const scriptProcessor = inputAudioContext.createScriptProcessor(
            4096,
            1,
            1,
          );

          scriptProcessor.onaudioprocess = (e) => {
            const inputData = e.inputBuffer.getChannelData(0);

            // Calculate volume for visualizer
            let sum = 0;
            for (let i = 0; i < inputData.length; i++)
              sum += Math.abs(inputData[i]);
            setVolumeLevel((sum / inputData.length) * 100);

            // Downsample to 16kHz for Gemini
            const downsampledData = downsampleTo16k(
              inputData,
              inputAudioContext.sampleRate,
            );
            const pcmBlob = createBlob(downsampledData);

            sessionPromise.then((session) => {
              session.sendRealtimeInput({ media: pcmBlob });
            });
          };

          source.connect(scriptProcessor);
          scriptProcessor.connect(inputAudioContext.destination);
        },
        onmessage: async (message: LiveServerMessage) => {
          // Handle Tool Calls (Station Changes)
          if (message.toolCall) {
            for (const fc of message.toolCall.functionCalls) {
              if (fc.name === "tuneStation") {
                console.log("DJ Changing station:", fc.args);
                setMode(DJMode.THINKING);
                onStationChangeRequest(
                  fc.args as {
                    genre?: string;
                    country?: string;
                    mood?: string;
                  },
                );

                // Confirm to model
                sessionPromise.then((session) => {
                  session.sendToolResponse({
                    functionResponses: {
                      id: fc.id,
                      name: fc.name,
                      response: {
                        result:
                          "Station recommendations found and list updated.",
                      },
                    },
                  });
                });
              }
            }
          }

          // Handle Audio Output
          const base64Audio =
            message.serverContent?.modelTurn?.parts[0]?.inlineData?.data;
          if (base64Audio) {
            setMode(DJMode.SPEAKING);
            const ctx = audioContextRef.current!;
            nextStartTimeRef.current = Math.max(
              nextStartTimeRef.current,
              ctx.currentTime,
            );

            // Model returns audio at 24000Hz
            const buffer = await decodeAudioData(base64Audio, ctx, 24000);
            const source = ctx.createBufferSource();
            source.buffer = buffer;
            source.connect(ctx.destination);

            source.addEventListener("ended", () => {
              sourcesRef.current.delete(source);
              if (sourcesRef.current.size === 0) {
                setMode(DJMode.LISTENING);
              }
            });

            source.start(nextStartTimeRef.current);
            nextStartTimeRef.current += buffer.duration;
            sourcesRef.current.add(source);
          }
        },
        onclose: () => {
          setIsConnected(false);
          setMode(DJMode.IDLE);
        },
        onerror: (e) => {
          console.error("Live API Error", e);
          setIsConnected(false);
          setMode(DJMode.IDLE);
        },
      },
      config: {
        responseModalities: [Modality.AUDIO],
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName } },
        },
        systemInstruction: systemInstruction,
        tools: [{ functionDeclarations: [tuneStationTool] }],
      },
    });

    sessionPromiseRef.current = sessionPromise;
  }, [onStationChangeRequest, persona, weatherContext, locationContext]);

  const disconnect = useCallback(() => {
    if (sessionPromiseRef.current) {
      inputContextRef.current?.close();
      audioContextRef.current?.close();
      setIsConnected(false);
      setMode(DJMode.IDLE);

      // Stop all sources
      sourcesRef.current.forEach((s) => s.stop());
      sourcesRef.current.clear();
    }
  }, []);

  return {
    connect,
    disconnect,
    isConnected,
    mode,
    volumeLevel,
  };
};
